import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, Shield, Sparkles, Trash2, FileCheck, Phone, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { services, testimonials, faqData, stadtbezirke } from '../mock';
import SEO from '../components/SEO';
import * as LucideIcons from 'lucide-react';

const Home = () => {
  const topFaqs = faqData.slice(0, 3);

  return (
    <div className="bg-white">
      <SEO 
        title="Haushaltsauflösung Dortmund – Schnell & zum Festpreis"
        description="Haushalts- & Wohnungsauflösungen in Dortmund zum Festpreis – besenrein, Wertanrechnung & Entsorgungsnachweis. Schnell, diskret, zuverlässig. ☎ 0231 39085116"
        canonical="/"
        keywords="Haushaltsauflösung Dortmund, Wohnungsauflösung Dortmund, Entrümpelung Dortmund, Festpreis, besenrein, Schössow Dienstleistungen"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6 leading-tight">
                Haushaltsauflösung in Dortmund – schnell, diskret & zum Festpreis
              </h1>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Haushaltsauflösung in Dortmund ohne Stress: Schössow Dienstleistungen räumt Wohnungen, 
                Häuser und Keller zügig, diskret und rechtssicher. Wir arbeiten zum fairen Festpreis, rechnen 
                Werte an und übergeben besenrein. Kontakt per Telefon 0231 39085116, WhatsApp oder E-Mail. 
                Fragen Sie in 60 Sekunden an – wir melden uns schnell mit einem verbindlichen Angebot.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-[#16A34A] hover:bg-[#16A34A]/90 text-white text-lg h-14"
                >
                  <Link to="/kontakt">Angebot in 60 Sek.</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white text-lg h-14"
                >
                  <a href="tel:+4923139085116" className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    0231 39085116
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white text-lg h-14"
                >
                  <a href="https://wa.me/4923139085116" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    WhatsApp
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7218003/pexels-photo-7218003.jpeg"
                alt="Professionelle Haushaltsauflösung Dortmund - Team bei der Arbeit"
                className="rounded-lg shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Overview for AI */}
      <section className="py-12 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Kurzfakten Haushaltsauflösung Dortmund</h2>
            <ul className="grid md:grid-cols-2 gap-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#16A34A] flex-shrink-0 mt-0.5" />
                <span><strong>Anbieter:</strong> Schössow Dienstleistungen, versichertes Team</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#16A34A] flex-shrink-0 mt-0.5" />
                <span><strong>Einsatzgebiet:</strong> Ganz Dortmund (alle 12 Stadtbezirke)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#16A34A] flex-shrink-0 mt-0.5" />
                <span><strong>Preisspannen:</strong> 1-Zi 600–1.200 €, 2-Zi 900–1.800 €, EFH 1.800–4.500 €*</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#16A34A] flex-shrink-0 mt-0.5" />
                <span><strong>Leistung:</strong> Besenrein, Entsorgungsnachweis, Wertanrechnung möglich</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#16A34A] flex-shrink-0 mt-0.5" />
                <span><strong>Termine:</strong> I.d.R. innerhalb weniger Tage, Express nach Absprache</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#16A34A] flex-shrink-0 mt-0.5" />
                <span><strong>Kontakt:</strong> Tel. 0231 39085116, WhatsApp, E-Mail</span>
              </li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">*Preis abhängig von Umfang/Zugänglichkeit; nach Besichtigung/Fotos verbindlicher Festpreis</p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-12">Ihre Vorteile</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-100 hover:border-[#16A34A] transition-colors">
              <CardContent className="pt-6">
                <CheckCircle className="h-12 w-12 text-[#16A34A] mb-4" />
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Festpreis-Garantie</h3>
                <p className="text-gray-700">Nach Besichtigung oder Foto-Zusendung erhalten Sie einen verbindlichen Festpreis. Keine versteckten Kosten.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-100 hover:border-[#16A34A] transition-colors">
              <CardContent className="pt-6">
                <Sparkles className="h-12 w-12 text-[#16A34A] mb-4" />
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Besenrein-Übergabe</h3>
                <p className="text-gray-700">Wir räumen vollständig, kehren und übergeben besenrein. Auf Wunsch mit Dokumentation.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-100 hover:border-[#16A34A] transition-colors">
              <CardContent className="pt-6">
                <Sparkles className="h-12 w-12 text-[#16A34A] mb-4" />
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Wertanrechnung möglich</h3>
                <p className="text-gray-700">Verwertbare Möbel, Antiquitäten oder Geräte werden transparent angerechnet und reduzieren Ihren Preis.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-100 hover:border-[#16A34A] transition-colors">
              <CardContent className="pt-6">
                <Clock className="h-12 w-12 text-[#16A34A] mb-4" />
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Express-Termine</h3>
                <p className="text-gray-700">Kurzfristige Räumungen innerhalb weniger Tage möglich. Wir arbeiten Mo–Sa 08–20 Uhr.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-100 hover:border-[#16A34A] transition-colors">
              <CardContent className="pt-6">
                <Trash2 className="h-12 w-12 text-[#16A34A] mb-4" />
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Gesetzeskonforme Entsorgung</h3>
                <p className="text-gray-700">Sortenreine Trennung und Entsorgung nach Vorschrift. Entsorgungsnachweise auf Wunsch.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-100 hover:border-[#16A34A] transition-colors">
              <CardContent className="pt-6">
                <Shield className="h-12 w-12 text-[#16A34A] mb-4" />
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Versichertes Team</h3>
                <p className="text-gray-700">Professionelle, erfahrene Mitarbeiter mit entsprechender Versicherung. Diskretion garantiert.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-4">So läuft’s ab</h2>
          <p className="text-center text-gray-700 mb-12 max-w-2xl mx-auto">In drei einfachen Schritten zur besenreinen Wohnung</p>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-[#16A34A] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">1</div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Anfragen</h3>
                <p className="text-gray-700">
                  Kontaktieren Sie uns telefonisch unter 0231 39085116, via WhatsApp oder per Formular. 
                  Ein paar Angaben oder Fotos helfen uns bei der Einschätzung.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-[#16A34A] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">2</div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Festpreis erhalten</h3>
                <p className="text-gray-700">
                  Nach einer Besichtigung vor Ort oder per Video/Foto erhalten Sie einen verbindlichen Preis 
                  inklusive Entsorgung. Transparent und fair kalkuliert.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-[#16A34A] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">3</div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Räumung & Abnahme</h3>
                <p className="text-gray-700">
                  Wir räumen termingerecht, sorgfältig und besenrein. Auf Wunsch übernehmen wir Demontagen 
                  und dokumentieren die fachgerechte Entsorgung.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-4">Unsere Leistungen in Dortmund</h2>
          <p className="text-center text-gray-700 mb-12 max-w-2xl mx-auto">
            Von der Haushaltsauflösung bis zur Entrümpelung – alles aus einer Hand
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Link key={service.id} to={`/${service.slug}`}>
                  <Card className="h-full border-2 border-gray-100 hover:border-[#16A34A] hover:shadow-lg transition-all">
                    <CardContent className="pt-6">
                      {IconComponent && <IconComponent className="h-10 w-10 text-[#16A34A] mb-4" />}
                      <h3 className="text-xl font-semibold text-[#0F172A] mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4">{service.description}</p>
                      <span className="text-[#16A34A] font-medium hover:underline">Mehr erfahren →</span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-4">Preise – fair & transparent</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Jede Auflösung ist anders. Unsere Festpreise berücksichtigen Größe, Zugänglichkeit, Sortieraufwand 
            und Sonderposten. Nach kurzer Abstimmung erhalten Sie ein verbindliches Angebot.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white border-2 border-gray-200">
              <CardContent className="pt-6 text-center">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-2">1-Zimmer-Wohnung</h3>
                <p className="text-3xl font-bold text-[#16A34A] mb-2">600 – 1.200 €</p>
                <p className="text-sm text-gray-600">Abhängig von Umfang & Zustand</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-2 border-[#16A34A]">
              <CardContent className="pt-6 text-center">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-2">2-Zimmer-Wohnung</h3>
                <p className="text-3xl font-bold text-[#16A34A] mb-2">900 – 1.800 €</p>
                <p className="text-sm text-gray-600">Inkl. besenreiner Übergabe</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-2 border-gray-200">
              <CardContent className="pt-6 text-center">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-2">Einfamilienhaus</h3>
                <p className="text-3xl font-bold text-[#16A34A] mb-2">1.800 – 4.500 €</p>
                <p className="text-sm text-gray-600">Nach Besichtigung Festpreis</p>
              </CardContent>
            </Card>
          </div>
          <p className="text-center text-gray-700 mb-6">
            Für ein präzises Angebot prüfen wir Ihre Angaben per Besichtigung, Fotos oder Video – schnell und transparent. 
            Wertanrechnung ist möglich und reduziert Ihren Endpreis.
          </p>
          <div className="text-center">
            <Button asChild size="lg" className="bg-[#16A34A] hover:bg-[#16A34A]/90 text-white">
              <Link to="/preise">Alle Preise & Details</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-4">Einsatzgebiet Dortmund</h2>
          <p className="text-center text-gray-700 mb-8 max-w-2xl mx-auto">
            Wir arbeiten im gesamten Stadtgebiet Dortmund – alle 12 Stadtbezirke:
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {stadtbezirke.map((bezirk, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full text-sm font-medium"
              >
                {bezirk}
              </span>
            ))}
          </div>
          <div className="text-center">
            <Button asChild variant="outline" className="border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white">
              <Link to="/einsatzgebiet-dortmund">Mehr zum Einsatzgebiet</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-12">Kundenstimmen</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white">
                <CardContent className="pt-6">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-[#16A34A] text-lg">★</span>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">„{testimonial.text}“</p>
                  <div className="font-semibold text-[#0F172A]">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.location}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-4">Häufige Fragen</h2>
          <p className="text-center text-gray-700 mb-12">Schnelle Antworten auf die wichtigsten Fragen</p>
          <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
            {topFaqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="px-6 text-left text-[#0F172A] font-semibold hover:text-[#16A34A]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-700">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Button asChild variant="outline" className="border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white">
              <Link to="/faq">Alle FAQs ansehen</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#0F172A] text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Jetzt Angebot anfordern</h2>
          <p className="text-lg mb-8 text-gray-300">
            Kontaktieren Sie uns per Telefon, WhatsApp oder Formular. Wir melden uns schnell mit Ihrem 
            verbindlichen Festpreis-Angebot.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-[#16A34A] hover:bg-[#16A34A]/90 text-white"
            >
              <a href="tel:+4923139085116" className="flex items-center justify-center gap-2">
                <Phone className="h-5 w-5" />
                0231 39085116
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white text-[#0F172A] hover:bg-gray-100"
            >
              <a href="https://wa.me/4923139085116" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                <MessageSquare className="h-5 w-5" />
                WhatsApp
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white text-[#0F172A] hover:bg-gray-100"
            >
              <Link to="/kontakt" className="flex items-center justify-center gap-2">
                <FileCheck className="h-5 w-5" />
                Zum Formular
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;